import React, { useEffect, useState, useMemo } from 'react';
import { Paper } from '@mui/material';
import automowerFirestoreService from '../services/automowerFirestoreService';
import AutomowerMap from '../components/AutomowerMap';
import PageShell, { Btn, Pill } from '../components/design/PageShell';
import { analyzeHistorySessions, fmtDuration, translateStatus, translateActivity } from '../utils/automowerAnalysis';

const ACCOUNT_ID = 'd3888a77-028b-41d9-8721-9da2c1daf767';

const statusLabel = (s) => {
  if (!s) return 'Inconnu';
  return translateStatus(s);
};

const statusTone = (s) => {
  if (s === 'MOWING' || s === 'IN_OPERATION') return 'ok';
  if (s === 'CHARGING' || s === 'PARKED_IN_CS' || s === 'PARKED' || s === 'RESTRICTED') return 'accent';
  if (s === 'ERROR' || s === 'FATAL_ERROR') return 'danger';
  return 'muted';
};

// Color tone by session type for the chronologie/sessions cards
const sessionTone = (type) => {
  switch (type) {
    case 'mowing':   return { color: 'var(--ok)',     label: '● Tonte' };
    case 'charging': return { color: 'var(--accent)', label: '● Charge' };
    case 'moving':   return { color: 'var(--warn)',   label: '● Déplacement' };
    case 'parked':   return { color: 'var(--ink-3)',  label: '● Stationné' };
    default:         return { color: 'var(--ink-2)',  label: '● Activité' };
  }
};

const Automower = () => {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [availableDates, setAvailableDates] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const dates = await automowerFirestoreService.getAvailableDates(ACCOUNT_ID);
        setAvailableDates(dates || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    const unsubs = [];
    const posU = automowerFirestoreService.subscribeToCurrentPosition(ACCOUNT_ID, (res) => {
      setLoading(false);
      if (res?.success && res.data) {
        const validLat = typeof res.data.lat === 'number' && !isNaN(res.data.lat);
        const validLng = typeof res.data.lng === 'number' && !isNaN(res.data.lng);
        setCurrent({ ...res.data, hasGPS: validLat && validLng });
      } else {
        setCurrent(null);
      }
    });
    unsubs.push(posU);

    const zonesU = automowerFirestoreService.subscribeToZones(ACCOUNT_ID, (z) => {
      const arr = Array.isArray(z) ? z : (z?.zones || []);
      setZones(arr);
    });
    unsubs.push(zonesU);

    const isToday = selectedDate === new Date().toISOString().split('T')[0];
    const histU = isToday
      ? automowerFirestoreService.subscribeToTrackingHistory(ACCOUNT_ID, 24, (h) => {
          const arr = Array.isArray(h) ? h : (h?.points || []);
          setHistory(arr);
        })
      : automowerFirestoreService.subscribeToTodayHistory(ACCOUNT_ID, selectedDate, (h) => {
          const arr = Array.isArray(h) ? h : (h?.points || []);
          setHistory(arr);
        });
    unsubs.push(histU);

    return () => unsubs.forEach(u => u && u());
  }, [selectedDate]);

  const battery = current?.battery ?? current?.batteryPercent ?? 0;
  const status = current?.status || current?.state;
  const currentZone = current?.workAreaName || current?.currentWorkAreaName || null;

  const analysis = useMemo(() => analyzeHistorySessions(history), [history]);
  const summary = analysis.summary;

  const isToday = selectedDate === new Date().toISOString().split('T')[0];
  const dateLabel = isToday
    ? "aujourd'hui"
    : new Date(selectedDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <PageShell
      section="automower"
      number="08"
      title="Automower"
      subtitle="Husqvarna · suivi temps réel · zones de tonte"
      actions={
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          style={{
            padding: '8px 12px', background: 'var(--bg-1)', color: 'var(--ink)',
            border: '1px solid var(--rule-2)', fontFamily: 'var(--font-mono)', fontSize: 12,
          }}
        />
      }
    >
      {loading && <div className="mono" style={{ color: 'var(--ink-3)' }}>Chargement…</div>}

      {!loading && (
        <>
          {/* Status grid (4 cards) */}
          <section className="ta-am-big" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, border: '1px solid var(--rule)' }}>
            <div style={{ padding: 28, borderRight: '1px solid var(--rule)', minHeight: 160 }}>
              <div className="eyebrow">{"// "}État</div>
              <div style={{ marginTop: 20 }}>
                <Pill tone={statusTone(status)}>{statusLabel(status)}</Pill>
              </div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 16, letterSpacing: '0.08em' }}>
                {translateActivity(current?.activity, { status }) || (current?.mode || 'mode principal')}
              </div>
            </div>
            <div style={{ padding: 28, borderRight: '1px solid var(--rule)' }}>
              <div className="eyebrow">{"// "}Batterie</div>
              <div className="mono tnum" style={{ fontSize: 44, fontWeight: 500, letterSpacing: '-0.02em', marginTop: 16, lineHeight: 1 }}>
                {battery}<span style={{ fontSize: 20, color: 'var(--ink-3)', marginLeft: 4 }}>%</span>
              </div>
              <div style={{ marginTop: 14, height: 3, background: 'var(--rule)' }}>
                <div style={{
                  height: '100%',
                  width: `${battery}%`,
                  background: battery > 50 ? 'var(--ok)' : battery > 20 ? 'var(--warn)' : 'var(--danger)',
                  transition: 'width 0.3s ease',
                }} />
              </div>
            </div>
            <div style={{ padding: 28, borderRight: '1px solid var(--rule)' }}>
              <div className="eyebrow">{"// "}Zone actuelle</div>
              <div className="mono tnum" style={{ fontSize: 44, fontWeight: 500, letterSpacing: '-0.02em', marginTop: 16, lineHeight: 1 }}>
                {currentZone || '—'}
              </div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10, letterSpacing: '0.08em' }}>
                {summary.zonesWorked.length}/{Math.max(zones.length, summary.zonesWorked.length, 4)} zones tondues
              </div>
            </div>
            <div style={{ padding: 28 }}>
              <div className="eyebrow">{"// "}Temps sélectionné</div>
              <div className="mono tnum" style={{ fontSize: 44, fontWeight: 500, letterSpacing: '-0.02em', marginTop: 16, lineHeight: 1 }}>
                {fmtDuration(summary.totalMowingTime)}
              </div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10, letterSpacing: '0.08em' }}>
                {summary.totalSessions} session{summary.totalSessions > 1 ? 's' : ''} · {summary.totalPoints} pts GPS
              </div>
            </div>
          </section>

          {/* Map — wrapped in Paper to mimic the original DOM context that works */}
          <div className="eyebrow" style={{ marginTop: 56, marginBottom: 20 }}>§ 08.A · Localisation &amp; tracé</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', border: '1px solid var(--rule)', borderBottom: 0 }}>
            <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.14em' }}>
              LAT {current?.lat?.toFixed?.(4) || '—'} · LON {current?.lng?.toFixed?.(4) || '—'}
            </span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.14em' }}>
              {history.length} POINTS · {isToday ? '24h' : selectedDate}
            </span>
          </div>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 0,
              overflow: 'hidden',
              background: 'transparent',
              border: '1px solid var(--rule)',
              borderTop: 0,
              borderBottom: 0,
            }}
          >
            <AutomowerMap
              currentPosition={current}
              historyPoints={history}
              zones={zones}
              zoom={19}
              showTrail={true}
              showZones={true}
              selectedDate={selectedDate}
            />
          </Paper>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', border: '1px solid var(--rule)', borderTop: 0 }}>
            <span className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', letterSpacing: '0.1em' }}>
              GOOGLE MAPS · SATELLITE
            </span>
            <span className="mono" style={{ fontSize: 10, color: current?.hasGPS ? 'var(--ok)' : 'var(--ink-4)', letterSpacing: '0.1em' }}>
              {current?.hasGPS ? '● LIVE' : '○ OFFLINE'}
            </span>
          </div>

          {/* Statistics 2-col layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 32, marginTop: 56 }} className="ta-am-stats-layout">
            {/* Left: chronologie */}
            <div>
              <div className="eyebrow" style={{ marginBottom: 20 }}>§ 08.B · Chronologie détaillée — {dateLabel}</div>
              <section style={{ border: '1px solid var(--rule)' }}>
                {analysis.timeline.length === 0 && (
                  <div className="mono" style={{ padding: 24, color: 'var(--ink-3)' }}>
                    Aucun événement enregistré
                  </div>
                )}
                <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                  {analysis.timeline.map((ev, i) => {
                    const tone = sessionTone(ev.type);
                    return (
                      <div key={i} style={{
                        display: 'grid',
                        gridTemplateColumns: '60px 1fr auto',
                        gap: 16,
                        padding: '14px 18px',
                        borderBottom: i < analysis.timeline.length - 1 ? '1px solid var(--rule)' : 0,
                        alignItems: 'center',
                      }}>
                        <span className="mono tnum" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.1em' }}>
                          {ev.time}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: 'var(--ink)' }}>{ev.description}</div>
                          {ev.workArea && (
                            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 4, letterSpacing: '0.06em' }}>
                              ZONE · {ev.workArea}
                            </div>
                          )}
                        </div>
                        <span className="mono" style={{ fontSize: 10, color: tone.color, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                          {ev.battery !== null && ev.battery !== undefined ? `${ev.battery}%` : tone.label.replace('● ', '')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>

            {/* Right: stats panel */}
            <aside>
              <div className="eyebrow" style={{ marginBottom: 20 }}>§ 08.C · Statistiques</div>
              <section style={{ border: '1px solid var(--rule)' }}>
                <div style={{ padding: 24, borderBottom: '1px solid var(--rule)' }}>
                  <div className="eyebrow">Temps de tonte</div>
                  <div className="mono tnum" style={{ fontSize: 28, marginTop: 8, color: 'var(--ok)' }}>
                    {fmtDuration(summary.totalMowingTime)}
                  </div>
                </div>
                <div style={{ padding: 24, borderBottom: '1px solid var(--rule)' }}>
                  <div className="eyebrow">Temps de charge</div>
                  <div className="mono tnum" style={{ fontSize: 28, marginTop: 8, color: 'var(--accent)' }}>
                    {fmtDuration(summary.totalChargingTime)}
                  </div>
                </div>
                <div style={{ padding: 24 }}>
                  <div className="eyebrow" style={{ marginBottom: 12 }}>Activité du jour</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--rule)' }}>
                    <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>Sessions</span>
                    <span className="mono tnum" style={{ fontSize: 13, color: 'var(--ink)' }}>{summary.totalSessions}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--rule)' }}>
                    <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>Points GPS</span>
                    <span className="mono tnum" style={{ fontSize: 13, color: 'var(--ink)' }}>{summary.totalPoints}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--rule)' }}>
                    <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>Zones tondues</span>
                    <span className="mono tnum" style={{ fontSize: 13, color: 'var(--accent)' }}>{summary.zonesWorked.length}</span>
                  </div>
                </div>
              </section>

              {summary.zonesWorked.length > 0 && (
                <section style={{ border: '1px solid var(--rule)', borderTop: 0, padding: 20 }}>
                  <div className="eyebrow" style={{ marginBottom: 12 }}>Zones tondues</div>
                  {summary.zonesWorked.map((z, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '6px 0', fontSize: 12, color: 'var(--ink)',
                    }}>
                      <span style={{ width: 8, height: 8, background: 'var(--ok)', display: 'inline-block' }} />
                      <span>{z}</span>
                    </div>
                  ))}
                </section>
              )}
            </aside>
          </div>

          {/* Sessions detailed grid */}
          {analysis.sessions.length > 0 && (
            <>
              <div className="eyebrow" style={{ marginTop: 56, marginBottom: 4 }}>
                § 08.D · Sessions détaillées
              </div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.08em', marginBottom: 20 }}>
                {analysis.sessions.length} sessions · {dateLabel}
              </div>
              <section className="ta-am-sessions" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 0,
                borderTop: '1px solid var(--rule)',
                borderLeft: '1px solid var(--rule)',
              }}>
                {analysis.sessions.map((s, i) => {
                  const tone = sessionTone(s.type);
                  return (
                    <article key={s.id} style={{
                      padding: 20,
                      borderRight: '1px solid var(--rule)',
                      borderBottom: '1px solid var(--rule)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span className="mono" style={{ fontSize: 10, color: tone.color, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
                          {tone.label}
                        </span>
                        <span className="mono tnum" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.08em' }}>
                          {s.formattedDuration}
                        </span>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 500, marginTop: 16, color: 'var(--ink)' }}>
                        {s.description}
                      </div>
                      <div className="mono tnum" style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 8, letterSpacing: '0.04em' }}>
                        {s.startTimeFormatted} → {s.endTimeFormatted}
                      </div>
                      {s.battery !== null && s.battery !== undefined && (
                        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 6, letterSpacing: '0.06em' }}>
                          BATTERIE {s.battery}%{s.workArea ? ` · ${s.workArea}` : ''}
                        </div>
                      )}
                    </article>
                  );
                })}
              </section>
            </>
          )}

          {/* Available dates quick select */}
          {availableDates.length > 0 && (
            <>
              <div className="eyebrow" style={{ marginTop: 56, marginBottom: 20 }}>§ 08.E · Historique disponible</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {availableDates.slice(0, 30).map(d => (
                  <button
                    key={d}
                    onClick={() => setSelectedDate(d)}
                    className={selectedDate === d ? 'ta-btn ta-btn-accent' : 'ta-btn ta-btn-ghost'}
                    style={{ padding: '6px 12px', height: 32, fontSize: 11 }}
                  >
                    <span>{d}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <style>{`
        @media (max-width: 1024px) {
          .ta-am-stats-layout { grid-template-columns: 1fr !important; }
          .ta-am-sessions { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 768px) {
          .ta-am-big { grid-template-columns: 1fr 1fr !important; }
          .ta-am-big > div { border-right: 1px solid var(--rule) !important; border-bottom: 1px solid var(--rule) !important; }
          .ta-am-big > div:nth-child(2n) { border-right: 0 !important; }
          .ta-am-big > div:nth-last-child(-n+2) { border-bottom: 0 !important; }
          .ta-am-sessions { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </PageShell>
  );
};

export default Automower;
